import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type TourManagerService from './tour-manager';
import type TourStorageService from './tour-storage';
import type ThoughtBubbleStorageService from './thought-bubble-storage';
import type { TourConfig } from './tour-manager';
import type { ThoughtBubbleFlow } from '../types/thought-bubble';
import { toDriverStep } from '../types/thought-bubble';

/**
 * Runtime service for evaluating and triggering thought bubble flows.
 *
 * On each route transition, evaluates all active flows against:
 * - Current route (exact match or wildcard/glob pattern)
 * - Current domain (window.location.hostname)
 * - User targeting rules (company, employee attributes, first-login)
 * - Completion state (via tourStorage)
 *
 * Note: Role-based targeting is not yet implemented in this MVP.
 * The targeting system currently supports company/employee attribute rules
 * and first-login checks. Role support may be added in a future iteration.
 *
 * Delegates rendering to the existing TourManager + driver.js infrastructure.
 *
 * @memberof services
 */
export default class ThoughtBubbleRuntimeService extends Service {
  @service declare router: RouterService;
  @service declare tourManager: TourManagerService;
  @service declare tourStorage: TourStorageService;
  @service declare thoughtBubbleStorage: ThoughtBubbleStorageService;

  @tracked isInitialized = false;

  /**
   * Track flows dismissed during this session to avoid pestering users.
   */
  private dismissedThisSession = new Set<string>();

  /**
   * Bound handler for route change events.
   */
  private routeChangeHandler?: () => void;

  /**
   * Pending trigger timeout ID for cancellation on route changes.
   */
  private pendingTriggerTimeout?: ReturnType<typeof setTimeout>;

  /**
   * The user context object. Set during initialization by the consuming app.
   * Expected shape: { company?: { id: string }, roles: Array<{ relationshipType: string }>, ... }
   */
  private userContext: Record<string, unknown> | null = null;

  /**
   * Initialize the runtime service. Called after user context is available.
   * Subscribes to route transitions and evaluates flows on each change.
   *
   * @param userContext - An object with user/company/role info for targeting evaluation.
   */
  initialize(userContext: Record<string, unknown>) {
    if (this.isInitialized) return;

    this.userContext = userContext;
    this.isInitialized = true;

    this.routeChangeHandler = () => {
      this.evaluateAndTrigger();
    };
    this.router.on('routeDidChange', this.routeChangeHandler);

    // Also evaluate immediately for the current route
    this.evaluateAndTrigger();
  }

  /**
   * Tear down the service: unsubscribe from route events.
   */
  willDestroy() {
    super.willDestroy();
    if (this.routeChangeHandler) {
      this.router.off('routeDidChange', this.routeChangeHandler);
      this.routeChangeHandler = undefined;
    }
    if (this.pendingTriggerTimeout) {
      clearTimeout(this.pendingTriggerTimeout);
      this.pendingTriggerTimeout = undefined;
    }
  }

  // ─── Core Evaluation ────────────────────────────────────────────────────

  /**
   * Evaluate all active flows against the current context and trigger the
   * highest-priority matching flow that hasn't been completed or dismissed.
   */
  private async evaluateAndTrigger() {
    // Cancel any pending trigger from a previous route change
    if (this.pendingTriggerTimeout) {
      clearTimeout(this.pendingTriggerTimeout);
      this.pendingTriggerTimeout = undefined;
    }

    // Ensure flows are loaded from API (no-op for localStorage mode)
    await this.thoughtBubbleStorage.ensureLoaded();

    const currentRoute = this.router.currentRouteName ?? '';
    const currentUrl = this.router.currentURL ?? window.location.pathname;
    const currentPath = currentUrl.split('?')[0] ?? '';
    const currentDomain = window.location.hostname;

    const activeFlows = this.thoughtBubbleStorage.getActiveFlows();

    const matchingFlows = activeFlows.filter((flow) => {
      const routeMatch = this.matchesRoute(flow.route, currentRoute, currentPath);
      const domainMatch = this.matchesDomain(flow.enabledDomains, currentDomain);
      const targetingMatch = this.matchesTargetingRules(flow);
      const notCompleted = !this.tourStorage.isTourCompleted(flow.id);
      const notDismissed = !this.dismissedThisSession.has(flow.id);

      return routeMatch && domainMatch && targetingMatch && notCompleted && notDismissed;
    });

    // Show the highest-priority matching flow (one at a time)
    matchingFlows.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    const flowToShow = matchingFlows[0];
    if (!flowToShow) return;
    this.triggerFlow(flowToShow);
  }

  /**
   * Convert a ThoughtBubbleFlow to a TourConfig and start it via tourManager.
   */
  private triggerFlow(flow: ThoughtBubbleFlow) {
    const tourConfig: TourConfig = {
      id: flow.id,
      name: flow.name,
      steps: flow.steps.sort((a, b) => a.order - b.order).map(toDriverStep),
      driverOptions: {
        showProgress: flow.steps.length > 1,
        showButtons: flow.steps.length > 1 ? ['next', 'previous', 'close'] : ['next', 'close'],
        doneBtnText: flow.steps.length > 1 ? 'Done' : 'Got it',
        onDestroyed: () => {
          // Mark as dismissed this session regardless of completion (tourManager handles completion)
          this.dismissedThisSession.add(flow.id);
        },
      },
    };

    this.tourManager.registerTour(tourConfig);

    // Delay slightly to let the DOM settle after route transition
    this.pendingTriggerTimeout = setTimeout(() => {
      this.pendingTriggerTimeout = undefined;
      this.tourManager.startTour(flow.id);
    }, 200);
  }

  // ─── Route Matching ─────────────────────────────────────────────────────

  /**
   * Check if a flow's route pattern matches the current route or URL path.
   *
   * Accepts BOTH formats:
   * - URL paths: '/people', '/people/*', '/companies/123/**'
   * - Ember route names: 'authenticated.people.index', 'authenticated.companies.show.*'
   *
   * URL paths (starting with '/') are matched against the current URL pathname.
   * Ember route names (dot-separated) are matched against router.currentRouteName.
   *
   * Supports wildcards in both modes:
   * - '*' matches one segment
   * - '**' matches any depth of segments
   */
  matchesRoute(pattern: string, currentRoute: string, currentPath?: string) {
    if (!pattern) return false;

    const isUrlPattern = pattern.startsWith('/');

    if (isUrlPattern) {
      // Match against URL path
      const target = currentPath ?? window.location.pathname;
      return this.matchPattern(pattern, target, '/');
    }

    // Match against Ember route name
    return this.matchPattern(pattern, currentRoute, '.');
  }

  /**
   * Generic pattern matching with a configurable separator.
   * Supports exact match, single wildcard (*), and globstar (**).
   */
  private matchPattern(pattern: string, value: string, separator: string) {
    // Normalize: strip trailing separator
    const normPattern = pattern.endsWith(separator) ? pattern.slice(0, -separator.length) : pattern;
    const normValue = value.endsWith(separator) ? value.slice(0, -separator.length) : value;

    // Exact match
    if (normPattern === normValue) return true;

    // Build regex: escape the separator, then handle wildcards
    const escapedSep = separator === '.' ? '\\.' : '\\/';
    const segmentChar = separator === '.' ? '[^.]+' : '[^/]+';

    const regexStr = normPattern
      .replace(separator === '.' ? /\./g : /\//g, escapedSep)
      .replace(/\*\*/g, '__GLOBSTAR__')
      .replace(/\*/g, segmentChar)
      .replace(new RegExp(`${escapedSep}__GLOBSTAR__`, 'g'), `(?:${escapedSep}.*)?`)
      .replace(/__GLOBSTAR__/g, '.*');

    try {
      const regex = new RegExp(`^${regexStr}$`);
      return regex.test(normValue);
    } catch {
      return false;
    }
  }

  // ─── Domain Matching ────────────────────────────────────────────────────

  /**
   * Check if the current domain is in the flow's enabled domains list.
   * Empty list = all domains allowed.
   */
  matchesDomain(enabledDomains: string[], currentDomain: string) {
    if (enabledDomains.length === 0) return true;
    return enabledDomains.some(
      (domain) => currentDomain === domain || currentDomain.endsWith(`.${domain}`)
    );
  }

  // ─── Targeting Rules ────────────────────────────────────────────────────

  /**
   * Evaluate targeting rules against the current user context.
   */
  matchesTargetingRules(flow: ThoughtBubbleFlow) {
    if (!this.userContext) return true;

    const { rules, matchMode, firstLoginOnly } = flow.targetingRules;
    const checks: boolean[] = [];

    // Evaluate each attribute rule
    for (const rule of rules) {
      const actual = this.getAttributeValue(rule.source, rule.attribute);
      const match = actual !== undefined && String(actual) === rule.value;
      checks.push(match);
    }

    // First login check
    if (firstLoginOnly) {
      checks.push(this.isFirstLogin());
    }

    // If no rules configured, allow all
    if (checks.length === 0) return true;

    const mode = matchMode || 'all';
    return mode === 'all' ? checks.every(Boolean) : checks.some(Boolean);
  }

  /**
   * Resolve an attribute value from the user context by source + attribute name.
   */
  private getAttributeValue(source: 'company' | 'employee', attribute: string): unknown {
    if (source === 'company') {
      const company = this.userContext?.['company'] as Record<string, unknown> | undefined;
      return company?.[attribute];
    }
    const employee = this.userContext?.['employee'] as Record<string, unknown> | undefined;
    return employee?.[attribute];
  }

  // ─── User Context Helpers ───────────────────────────────────────────────

  private isFirstLogin(): boolean {
    const logins = this.userContext?.['logins'] as Array<unknown> | undefined;
    // If login history is not available, we cannot safely determine first login
    if (!Array.isArray(logins)) {
      return false;
    }
    return logins.length <= 1;
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Manually dismiss a flow for this session.
   */
  dismissFlow(flowId: string) {
    this.dismissedThisSession.add(flowId);
  }

  /**
   * Reset all session dismissals (useful for testing).
   */
  resetDismissals() {
    this.dismissedThisSession.clear();
  }

  /**
   * Force-show a specific flow regardless of targeting/completion state.
   * Useful for preview/testing in the admin UI.
   */
  forceShowFlow(flowId: string) {
    const flow = this.thoughtBubbleStorage.getFlow(flowId);
    if (!flow) {
      console.warn(`Thought bubble flow not found: ${flowId}`);
      return;
    }
    this.triggerFlow(flow);
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-runtime': ThoughtBubbleRuntimeService;
  }
}
